
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Member } from "@/types/member";
import { useTranslation } from "react-i18next";

interface ProfileData {
  id: string;
  name: string | null;
  username: string | null;
  avatar_url: string | null;
}

interface MemberData {
  id: string;
  is_admin: boolean;
  profiles: ProfileData;
}

export const useGroupMembers = (groupId: string | undefined) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Fetch members function
  const fetchMembers = useCallback(async () => {
    if (!groupId) return;
    
    console.log('Fetching members...');
    try {
      const { data: membersData, error: membersError } = await supabase
        .from('group_members')
        .select(`
          id,
          is_admin,
          profiles:profile_id (
            id,
            username,
            name,
            avatar_url
          )
        `)
        .eq('group_id', groupId);
      
      if (membersError) {
        console.error('Error fetching members:', membersError);
        return;
      }
      
      if (!membersData) {
        console.log("No members data returned");
        return;
      }
      
      console.log('Setting members:', membersData);
      
      const formattedMembers: Member[] = membersData.map(m => {
        // Ensure we handle profiles data correctly
        const profile = m.profiles as unknown as ProfileData;
        
        return {
          id: profile.id,
          name: profile.name || 'Unnamed User',
          username: profile.username ? profile.username.split('@')[0] : 'anonymous',
          isAdmin: m.is_admin || false,
          avatar_url: profile.avatar_url || ''
        };
      });
      
      setMembers(formattedMembers);
    } catch (error) {
      console.error("Error in fetchMembers:", error);
    }
  }, [groupId]);

  // Handle removing a member
  const handleRemoveMember = useCallback(async (memberId: string) => {
    if (!groupId) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: t('common.error'),
          description: t('auth.notLoggedIn', 'You must be logged in'),
          variant: "destructive"
        });
        return;
      }
      
      // Check if current user is admin
      const { data: currentMember, error: memberError } = await supabase
        .from('group_members')
        .select('is_admin')
        .eq('group_id', groupId)
        .eq('profile_id', session.user.id)
        .maybeSingle();
      
      if (memberError) {
        console.error('Error checking admin status:', memberError);
        toast({
          title: t('common.error'),
          description: t('groups.errorCheckingPermissions', 'Error checking permissions'),
          variant: "destructive"
        });
        return;
      }
      
      if (!currentMember?.is_admin) {
        toast({
          title: t('common.error'),
          description: t('groups.onlyAdminsCanRemove', 'Only admins can remove members'),
          variant: "destructive"
        });
        return;
      }
      
      // Prevent removing the last admin
      const adminMember = members.find(m => m.id === memberId);
      if (adminMember?.isAdmin) {
        const adminCount = members.filter(m => m.isAdmin).length;
        if (adminCount <= 1) {
          toast({
            title: t('common.error'),
            description: t('groups.cannotRemoveLastAdmin', 'Cannot remove the last admin from the group'),
            variant: "destructive"
          });
          return;
        }
      }
      
      // Optimistically update the UI before the actual removal
      setMembers(prevMembers => prevMembers.filter(member => member.id !== memberId));
      
      // Remove the member
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('profile_id', memberId);
      
      if (error) {
        console.error('Error removing member:', error);
        toast({
          title: t('common.error'),
          description: t('groups.failedToRemoveMember', 'Failed to remove member'),
          variant: "destructive"
        });
        
        // Revert the optimistic update if there was an error
        fetchMembers();
        return;
      }
      
      toast({
        description: t('groups.memberRemoved', 'Member removed successfully'),
      });
      
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: t('common.error'),
        description: t('groups.failedToRemoveMember', 'Failed to remove member'),
        variant: "destructive"
      });
      
      // Revert any optimistic updates
      fetchMembers();
    }
  }, [groupId, members, toast, t, fetchMembers]);

  return {
    members,
    showAddMember,
    showShareLink,
    setShowAddMember,
    setShowShareLink,
    fetchMembers,
    handleRemoveMember
  };
};
